import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSignatureRequest } from '@/api/hooks/useSignatureRequest'
import { useSubmitSignature } from '@/api/hooks/useSubmitSignature'
import SignatureCanvas from '@/components/molecules/signature-canvas'
import { Button } from '@/components/ui/button'

export const Route = createFileRoute('/signatures/$signatureId')({
  component: SignaturePage,
})

function SignaturePage() {
  const { t } = useTranslation()
  const { signatureId } = Route.useParams()
  const [dataUrl, setDataUrl] = useState<string | null>(null)
  const [submitted, setSubmitted] = useState(false)

  const { isPending, state } = useSignatureRequest(signatureId)
  const submitMutation = useSubmitSignature()

  if (isPending) {
    return (
      <PageShell>
        <div className="text-sm text-text-secondary">{t('global.loading')}</div>
      </PageShell>
    )
  }

  if (!state || state.status === 'not_found') {
    return (
      <PageShell>
        <ErrorCard
          title={t('signaturePage.notFoundTitle')}
          description={t('signaturePage.notFoundDescription')}
        />
      </PageShell>
    )
  }

  if (state.status === 'expired') {
    return (
      <PageShell>
        <ErrorCard
          title={t('signaturePage.expiredTitle')}
          description={t('signaturePage.expiredDescription')}
        />
      </PageShell>
    )
  }

  if (submitted) {
    return (
      <PageShell>
        <div className="card">
          <h1 className="heading-4">{t('signaturePage.successTitle')}</h1>
          <p className="body-1 text-text-secondary">{t('signaturePage.successDescription')}</p>
        </div>
      </PageShell>
    )
  }

  const { clientName, serviceAgreement } = state.data

  return (
    <PageShell>
      <div className="card w-full max-w-md flex flex-col gap-4">
        <div>
          <h1 className="text-lg font-semibold">{t('signaturePage.title')}</h1>
          <p className="text-sm text-text-secondary mt-1">
            {clientName
              ? t('signaturePage.instruction', { name: clientName })
              : t('signaturePage.instructionNoName')}
          </p>
        </div>
        {serviceAgreement && (
          <div className="card bg-layer-2">
            <h2 className="heading-4">{serviceAgreement.title}</h2>
            {serviceAgreement.definition && <p className="body-2">{serviceAgreement.definition}</p>}
          </div>
        )}
        <SignatureCanvas
          value={dataUrl}
          onSave={setDataUrl}
          label={t('global.signature')}
          previewHeight={200}
        />
        <Button
          type="button"
          disabled={submitMutation.isPending || !dataUrl}
          onClick={async () => {
            if (!dataUrl) return
            await submitMutation.mutateAsync(
              { path: { token: signatureId }, body: { image: dataUrl } },
              { onSuccess: () => setSubmitted(true) },
            )
          }}
        >
          {submitMutation.isPending
            ? t('signaturePage.submittingButton')
            : t('signaturePage.submitButton')}
        </Button>
      </div>
    </PageShell>
  )
}

function PageShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      {children}
    </div>
  )
}

function ErrorCard({ title, description }: { title: string; description: string }) {
  return (
    <div className="card">
      <h1 className="heading-4">{title}</h1>
      <p className="body-1 text-text-secondary">{description}</p>
    </div>
  )
}
